import { useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { UserContext } from '~/contexts/UserContext';
import { PrepApi } from '~/api/prep';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/shadcn/components/ui/Table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/shadcn/components/ui/DropdownMenu';
import { Button } from '~/shadcn/components/ui/Button';
import { Badge } from '~/shadcn/components/ui/Badge';
import { MoreHorizontal, AlertCircle } from 'lucide-react';
import PrepActionModal from './PrepActionModal';
import PrepPalletModal from '../prepModal/PrepPalletModal';
import MultipleLotWarningModal from './MultipleLotWarningModal';
import { MovePalletAction } from '~/components/warehouse/components/locationInventoryV2Components/actions/movePalletAction';
import { PrintPalletLabelAction } from '~/components/warehouse/components/locationInventoryV2Components/actions/printPalletLabelAction';

const LocationBadge = ({ location }) => (
    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 rounded-md font-semibold">{location}</Badge>
);

function PrepInboundTable({ locationFilter }) {
    const { user } = useContext(UserContext);
    const prepApi = new PrepApi();

    const [inboundData, setInboundData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [isPrepModalOpen, setIsPrepModalOpen] = useState(false);
    const [isMultipleLotWarningOpen, setIsMultipleLotWarningOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [scrapReasons, setScrapReasons] = useState([]);

    useEffect(() => {
        const fetchScrapReasons = async () => {
            const response = await prepApi.getPrepScrapReasons();
            if (!response.status) {
                toast.error(response.msg || 'Failed to fetch scrap reasons.');
                return;
            }
            setScrapReasons(response.data?.reasons?.map((reason) => ({ value: reason, label: reason })));
        };

        fetchScrapReasons();
    }, []);

    const fetchInboundPallets = useCallback(async () => {
        if (!user?.facilityID) return;

        try {
            setIsLoading(true);
            const response = await prepApi.getPalletsInPrepLocations({
                facilityId: user.facilityID,
                type: 'Inbound',
                locationName: locationFilter,
            });
            if (response.status) {
                setInboundData(response.data.rows);
            } else {
                setError('Failed to fetch inbound pallets.');
            }
        } catch (err) {
            setError(err.message || 'An unexpected error occurred.');
        } finally {
            setIsLoading(false);
        }
    }, [user?.facilityID, locationFilter]);

    // Check if a pallet has multiple lots
    const hasMultipleLots = (palletId) => {
        const lotsOnPallet = inboundData.filter((row) => row.palletId === palletId);
        return lotsOnPallet.length > 1;
    };

    const handleActionClick = (action, rowData) => {
        setSelectedAction(action);
        setSelectedRowData(rowData);

        if (action === 'Prep Pallet') {
            // Check if this pallet has multiple lots
            if (hasMultipleLots(rowData.palletId)) {
                setIsMultipleLotWarningOpen(true);
            } else {
                setIsPrepModalOpen(true);
            }
        } else {
            setIsActionModalOpen(true);
        }
    };

    const handleSuccess = () => {
        setIsActionModalOpen(false);
        fetchInboundPallets();
        toast.success('Action completed successfully!');
    };

    const handlePrepSuccess = () => {
        fetchInboundPallets();
    };

    const renderModalContent = () => {
        if (!selectedAction) return null;

        switch (selectedAction) {
            case 'Print Pallet Label':
                return <PrintPalletLabelAction palletId={selectedRowData?.palletId} onSuccess={handleSuccess} />;
            case 'Move Pallet':
                return (
                    <MovePalletAction
                        palletId={selectedRowData?.palletId}
                        onSuccess={handleSuccess}
                        inNonMuiModal={true}
                    />
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        fetchInboundPallets();
    }, [fetchInboundPallets]);

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center text-center py-10">
                <AlertCircle className="h-10 w-10 text-red-500 mb-3" />
                <p className="text-lg font-semibold text-red-600">Failed to load data</p>
                <p className="text-muted-foreground">{error}</p>
            </div>
        );
    }

    if (!isLoading && inboundData.length === 0) {
        return (
            <div className="text-center py-10">
                <p className="text-lg font-semibold">No Prep Inbound Pallets Found</p>
                <p className="text-muted-foreground">
                    There are no pallets in prep inbound locations{locationFilter ? ` for ${locationFilter}` : ''}.
                </p>
            </div>
        );
    }

    return (
        <>
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        <TableHead>PALLET</TableHead>
                        <TableHead>LOT</TableHead>
                        <TableHead>ITEM</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>LOCATION</TableHead>
                        <TableHead>EXPIRATION</TableHead>
                        <TableHead>CASES</TableHead>
                        <TableHead>UNITS PER CASE</TableHead>
                        <TableHead>LOOSE UNITS</TableHead>
                        <TableHead>TOTAL UNITS</TableHead>
                        <TableHead>ACTIONS</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {inboundData.map((row) => (
                        <TableRow key={row.palletId}>
                            <TableCell className="font-medium">{row.palletId}</TableCell>
                            <TableCell className="font-medium">{row.lotId}</TableCell>
                            <TableCell>{row.itemName}</TableCell>
                            <TableCell className="font-medium">{row.sku}</TableCell>
                            <TableCell>
                                <LocationBadge location={row.locationName} />
                            </TableCell>
                            <TableCell className="font-medium">{row.expirationDate}</TableCell>
                            <TableCell className="font-medium">{row.numCases}</TableCell>
                            <TableCell className="font-medium">{row.unitsPerCase}</TableCell>
                            <TableCell className="font-medium">{row.numLooseUnits}</TableCell>
                            <TableCell className="font-medium">{row.totalUnits}</TableCell>
                            <TableCell>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="h-8 w-8 p-0">
                                            <span className="sr-only">Open menu</span>
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => handleActionClick('Prep Pallet', row)}>
                                            Prep Pallet
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleActionClick('Print Pallet Label', row)}>
                                            Print Pallet Label
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleActionClick('Move Pallet', row)}>
                                            Move Pallet
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <PrepActionModal
                isOpen={isActionModalOpen}
                onOpenChange={setIsActionModalOpen}
                title={selectedAction || ''}
            >
                {renderModalContent()}
            </PrepActionModal>
            {selectedRowData && (
                <PrepPalletModal
                    isOpen={isPrepModalOpen}
                    onOpenChange={setIsPrepModalOpen}
                    palletData={selectedRowData}
                    onSuccess={handlePrepSuccess}
                    scrapReasons={scrapReasons}
                />
            )}
            {selectedRowData && (
                <MultipleLotWarningModal
                    open={isMultipleLotWarningOpen}
                    onOpenChange={setIsMultipleLotWarningOpen}
                    lotId={selectedRowData.lotId}
                    palletId={selectedRowData.palletId}
                />
            )}
        </>
    );
}

export default PrepInboundTable;
